import { motion } from 'framer-motion';
import { Clock, ArrowRight, AlertTriangle } from 'lucide-react';
import { COMPLIANCE_CYCLE, CCC_ISSUANCE, TRADING_INFRA, THREE_CALLOUTS } from '@/data/cctsData';

export const ComplianceCycleSection = () => {
  return (
    <div data-testid="compliance-cycle-section" className="space-y-12">

      {/* ═══ SECTION 1: 5-Step Compliance Cycle — Sticky Scroll ═══ */}
      <div>
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Sticky left heading */}
          <div className="lg:col-span-4 lg:sticky lg:top-24 lg:h-fit">
            <p className="font-mono text-xs text-gray-400 uppercase tracking-[0.15em] mb-3">The Annual Cycle</p>
            <h3 className="font-serif text-3xl md:text-4xl text-brand-dark tracking-tight leading-tight">Five steps,<br />one hard <span className="text-brand-brown italic">deadline.</span></h3>
            <div className="flex items-center gap-2 mt-4 text-brand-brown">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-sans text-sm font-bold">Hard deadline: 31 Jul 2026</span>
            </div>
          </div>

          {/* Scrollable right cards */}
          <div className="lg:col-span-8 space-y-4">
            {COMPLIANCE_CYCLE.map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className={`relative rounded-2xl p-8 md:p-10 overflow-hidden ${c.hard ? 'bg-[#a6533a] text-white' : 'bg-white border border-gray-200'}`}
                data-testid={`compliance-step-${i}`}
              >
                {/* Ghost number */}
                <span aria-hidden="true" className={`absolute bottom-2 right-6 font-serif text-[120px] leading-none font-bold pointer-events-none select-none ${c.hard ? 'text-white/10' : 'text-[#a6533a]/[0.05]'}`}>
                  {String(c.step).padStart(2, '0')}
                </span>
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`font-mono text-xs px-3 py-1 rounded-full ${c.hard ? 'bg-white/20 text-white' : 'bg-brand-green/10 text-brand-green'}`}>{c.date}</span>
                    {c.hard && <span className="font-mono text-[10px] bg-white text-brand-brown px-2.5 py-1 rounded-sm uppercase tracking-widest font-bold">Hard Deadline</span>}
                  </div>
                  <h4 className={`font-serif text-xl md:text-2xl tracking-tight ${c.hard ? 'text-white' : 'text-brand-dark'}`}>{c.title}</h4>
                  <p className={`font-sans text-sm mt-2 leading-relaxed ${c.hard ? 'text-white/80' : 'text-gray-500'}`}>{c.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ SECTION 2: CCC Issuance — Editorial Ribbon ═══ */}
      <div>
        <p className="font-mono text-xs text-gray-400 uppercase tracking-[0.15em] mb-2">From Verification to Registry</p>
        <h3 className="font-serif text-3xl md:text-4xl text-brand-dark tracking-tight mb-8">CCC Issuance <span className="italic text-brand-green">Pathway</span></h3>
        <div className="relative">
          {/* Connecting dashed line */}
          <div className="absolute top-10 left-[calc(12.5%)] right-[calc(12.5%)] border-t-2 border-dashed border-brand-green/30 hidden md:block" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {CCC_ISSUANCE.map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="relative"
              >
                {/* Node on the line */}
                <div className="w-8 h-8 rounded-full bg-brand-green text-white flex items-center justify-center mx-auto mb-4 font-mono text-sm font-bold relative z-10 ring-4 ring-white">
                  {c.step}
                </div>
                <div className="bg-white border border-[#1a5c1e]/15 rounded-xl p-5 text-center shadow-[0_4px_20px_rgba(166,83,58,0.06)]">
                  <p className="font-sans text-base font-semibold text-brand-dark">{c.title}</p>
                  <p className="font-mono text-xs text-gray-400 mt-1">{c.sub}</p>
                </div>
                {i < 3 && <ArrowRight className="absolute -right-3 top-10 w-5 h-5 text-brand-green/40 hidden md:block z-10" />}
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ SECTION 3: Trading Infrastructure — Compact Horizontal ═══ */}
      <div>
        <p className="font-mono text-xs text-gray-400 uppercase tracking-[0.15em] mb-2">Market Plumbing</p>
        <h3 className="font-serif text-3xl md:text-4xl text-brand-dark tracking-tight mb-6">Trading <span className="italic text-brand-brown">Infrastructure</span></h3>
        <div className="relative">
          {/* Connecting dashed line */}
          <div className="absolute top-10 left-[calc(8%)] right-[calc(8%)] border-t-2 border-dashed border-brand-brown/25 hidden md:block" />
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {TRADING_INFRA.map((t, i) => {
              const labels = ['CERC', 'Registry', 'Exchanges', 'Mechanism', 'Pricing', 'Allocation'];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="relative"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-4 font-mono text-sm font-bold relative z-10 ring-4 ring-white ${i < 2 ? 'bg-[#1a5c1e] text-white' : i < 4 ? 'bg-brand-brown text-white' : 'bg-brand-green/20 text-brand-green'}`}>
                    {i + 1}
                  </div>
                  <div className="bg-white border border-gray-200 rounded-xl p-4 text-center shadow-[0_2px_12px_rgba(166,83,58,0.04)]">
                    <p className="font-mono text-[10px] text-gray-400 uppercase tracking-widest mb-1">{labels[i]}</p>
                    <p className="font-sans text-xs text-gray-600 leading-relaxed">{t}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ═══ SECTION 4: Callouts — Editorial Pull-Quotes ═══ */}
      <div className="grid md:grid-cols-3 gap-4">
        {THREE_CALLOUTS.map((c, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
            className="bg-[#fbfbf9] border border-[#a6533a]/15 rounded-2xl p-6 shadow-[0_4px_20px_rgba(166,83,58,0.06)]"
          >
            <p className="font-serif text-base italic text-brand-brown leading-relaxed">{c.bold}</p>
            <p className="font-sans text-sm text-gray-500 mt-2">{c.rest}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
